import React, { useEffect, useRef, useState } from 'react';
import {
  LayoutGrid, Bookmark, BookmarkCheck, Home, TrendingUp, Calculator,
  Megaphone, ShoppingBag, Palette, Lightbulb, UsersRound, Bot,
} from 'lucide-react';
import { supabase } from '../lib/supabase';

// Run-local versions of the shared header actions (Run is CRA and can't import
// packages/shared): the app-switcher dropdown and the save-to-Hub bookmark.
// Same behavior as everywhere else — session tokens carry across apps, and
// saves land in hub_shortcuts labeled from the route.

const APPS = [
  { key: 'hub', name: 'Hub', description: 'Home', icon: Home, url: 'https://daydreamerconcepts.com' },
  { key: 'grow', name: 'Grow', description: 'Wholesale', icon: TrendingUp, url: 'https://grow.daydreamerconcepts.com' },
  { key: 'count', name: 'Count', description: 'Accounting', icon: Calculator, url: 'https://count.daydreamerconcepts.com' },
  { key: 'reach', name: 'Reach', description: 'Marketing', icon: Megaphone, url: 'https://reach.daydreamerconcepts.com' },
  { key: 'shop', name: 'Shop', description: 'E-Commerce', icon: ShoppingBag, url: 'https://shop.daydreamerconcepts.com/admin' },
  { key: 'custom', name: 'Custom', description: 'Private Label', icon: Palette, url: 'https://custom.daydreamerconcepts.com' },
  { key: 'create', name: 'Create', description: 'Product Dev', icon: Lightbulb, url: 'https://create.daydreamerconcepts.com' },
  { key: 'team', name: 'Team', description: 'People', icon: UsersRound, url: 'https://team.daydreamerconcepts.com' },
  { key: 'agents', name: 'Agents', description: 'AI Agents', icon: Bot, url: 'https://agents.daydreamerconcepts.com' },
];

const panelStyle = {
  position: 'fixed', left: 8, top: 60, width: 300, zIndex: 1200,
  background: '#fff', border: '1px solid #e5e5e5', borderRadius: 12,
  boxShadow: '0 8px 24px rgba(0,0,0,0.12)', padding: 8,
  display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 4,
};

const itemStyle = {
  display: 'flex', alignItems: 'center', gap: 10, padding: '10px 10px',
  borderRadius: 8, border: 'none', background: 'transparent',
  cursor: 'pointer', textAlign: 'left', width: '100%',
};

export default function HubActions() {
  const [open, setOpen] = useState(false);
  const [saveState, setSaveState] = useState('idle');
  const ref = useRef(null);

  useEffect(() => {
    if (!open) return undefined;
    const onDown = (e) => {
      if (ref.current && !ref.current.contains(e.target)) setOpen(false);
    };
    document.addEventListener('mousedown', onDown);
    return () => document.removeEventListener('mousedown', onDown);
  }, [open]);

  if (!supabase) return null;

  const go = async (url) => {
    try {
      const { data: { session } } = await supabase.auth.getSession();
      if (session) {
        const sep = url.includes('?') ? '&' : '?';
        window.location.href = `${url}${sep}hub_token=${encodeURIComponent(session.access_token)}&hub_refresh=${encodeURIComponent(session.refresh_token)}`;
        return;
      }
    } catch (e) { /* fall through */ }
    window.location.href = url;
  };

  const save = async () => {
    if (saveState !== 'idle') return;
    setSaveState('busy');
    try {
      const { data: { user } } = await supabase.auth.getUser();
      if (!user) throw new Error('not signed in');
      const url = window.location.origin + window.location.pathname;
      const { data: existing } = await supabase.from('hub_shortcuts').select('id').eq('url', url).limit(1);
      if (!existing || !existing.length) {
        const seg = window.location.pathname
          .split('/')
          .filter(Boolean)
          .filter((s) => !/^[0-9a-f-]{8,}$/i.test(s) && !/^\d+$/.test(s))
          .pop();
        const nice = seg ? seg.replace(/[-_]+/g, ' ').replace(/\b\w/g, (c) => c.toUpperCase()) : '';
        const { error } = await supabase.from('hub_shortcuts').insert({ user_id: user.id, label: nice || 'Run', url });
        if (error) throw error;
      }
      setSaveState('saved');
    } catch (e) {
      setSaveState('error');
    }
    setTimeout(() => setSaveState('idle'), 2200);
  };

  return (
    <div ref={ref} style={{ display: 'flex', alignItems: 'center', gap: 2 }}>
      <button
        className="nav-hub-link"
        title="Switch app"
        onClick={() => setOpen((o) => !o)}
        style={{ border: 'none', background: open ? '#f3ede5' : 'transparent', cursor: 'pointer' }}
      >
        <LayoutGrid size={16} />
      </button>
      <button
        className="nav-hub-link"
        title="Save this page to your Hub home"
        onClick={save}
        style={{ border: 'none', background: 'transparent', cursor: 'pointer' }}
      >
        {saveState === 'saved' ? <BookmarkCheck size={16} color="#8B6F4E" /> : <Bookmark size={16} />}
      </button>

      {open && (
        <div style={panelStyle}>
          {APPS.map((a) => {
            const Icon = a.icon;
            return (
              <button
                key={a.key}
                style={itemStyle}
                onClick={() => go(a.url)}
                onMouseEnter={(e) => { e.currentTarget.style.background = '#fafaf8'; }}
                onMouseLeave={(e) => { e.currentTarget.style.background = 'transparent'; }}
              >
                <span style={{
                  width: 30, height: 30, borderRadius: 8, background: '#f3ede5',
                  display: 'flex', alignItems: 'center', justifyContent: 'center', flexShrink: 0,
                }}>
                  <Icon size={15} color="#8B6F4E" />
                </span>
                <span style={{ minWidth: 0 }}>
                  <span style={{ display: 'block', fontSize: 13, fontWeight: 600, color: '#262626' }}>{a.name}</span>
                  <span style={{ display: 'block', fontSize: 11, color: '#a3a3a3' }}>{a.description}</span>
                </span>
              </button>
            );
          })}
        </div>
      )}
    </div>
  );
}
